/**
 * Catálogo de programas de rolesusuarios, derivado de routes.js.
 *
 * Cada pantalla del menú declara su programa en `key`/`keys`/`programa`;
 * este módulo recorre routes.js y arma, por programa, la etiqueta humana,
 * el módulo/sección al que pertenece, la `categoria` que debe llevar la
 * fila de rolesusuarios y las acciones (flags) que el código realmente
 * consulta para ese programa. Lo usan el asistente de "Nuevo rol" y el
 * autocompletado del editor de filas en usuariosroles.js.
 *
 * El catálogo se construye perezosamente (getCatalogoProgramas) porque
 * routes.js importa las pantallas y una de ellas (usuariosroles) importa
 * este módulo: evaluar routes en el top-level cerraría el ciclo antes de
 * que routes.js termine de evaluarse.
 */
import routes from "routes";
import routesFac from "routesfac";
import routesNom from "routesnom";

/** Entradas de routes.js que no son programas de rolesusuarios. */
const EXCLUIR = new Set(["sign-in", "sign-up", "dashboard-pantalla"]);

/**
 * Entradas de routesfac.js que no son programas propios: dashboard es
 * compartido y los módulos que también existen en routes.js (clientes /
 * suplidores) ya quedan registrados por la pasada de routes.
 */
const EXCLUIR_FAC = new Set(["sign-in", "sign-up", "dashboard"]);

/**
 * `categoria` de las filas de la app de Facturación POS. Es propia para no
 * chocar con las categorías que consultan las pantallas de préstamos por
 * puedeAccion ('facturacion', 'motores', 'gastos'): los flags se combinan
 * por categoría, así que compartirla concedería acciones por rebote.
 */
const CATEGORIA_FAC = "facturacionpos";

/**
 * `categoria` que deben llevar las filas de cada módulo. Por defecto es el
 * `programa`/`key` del módulo, pero Inventario usa "motores" porque los
 * chequeos por categoría del código (puedeAccion(roleDetalles, "motores", …))
 * y los datos históricos del escritorio ares usan ese nombre.
 */
const CATEGORIA_MODULO = { inventario: "motores" };

/**
 * Programas cuya `categoria` no es la de su módulo. Cobros Incautos Fechas
 * vive en el módulo Incautos pero sus botones se gobiernan con
 * puedeAccion(roleDetalles, "motores", …), así que su fila debe llevar
 * categoria "motores" para que ese chequeo encuentre los flags.
 */
const CATEGORIA_PROGRAMA = { cobrosincautosfechas: "motores" };

/**
 * Acciones (flags de rolesusuarios) que el código consulta por programa.
 * Fuente: usos de puedeAccion/puedeAccionPrograma en el frontend y el mapa
 * RUTAS_PROTEGIDAS de permisos.py en el server. Un programa que no aparece
 * aquí solo se usa como "tiene acceso a la pantalla" (la fila existe).
 */
const ACCIONES_PROGRAMA = {
  // Inventario
  // registro de chasis: modificar = guardar cambios de un chasis existente;
  // borrar = eliminar / reingresar; versucursal = elegir sucursal libre
  ares1010: ["modificar", "borrar", "versucursal"],
  marcasmodelos: ["modificar", "borrar"], // fusionar/aceptar variantes; borrar = eliminar del catálogo
  informemotores: ["modificar"], // cambiar sucursal del motor (modificasucursal)
  consignaciones: ["borrar"], // devolver mercancía al consignante
  // cerrar conteo / descartar conteo abierto; versucursal = elegir sucursal
  // (o todas) en el Reporte de conteos (sin el flag, el backend fuerza la
  // del usuario)
  conteos: ["modificar", "borrar", "versucursal"],
  // transferencias de chasis: modificar = recibir / devolver / actualizar
  // matrícula; borrar = cancelar autorización / eliminar recepción;
  // versucursal = Seguimiento y reportes de TODAS las sucursales (sin el
  // flag, el backend fuerza la sucursal del usuario)
  transferenciachasis: ["modificar", "borrar", "versucursal"],
  // importaciones de embarques: modificar = liquidar y recibir en inventario;
  // borrar = cancelar embarque / quitar chasis, pagos y documentos
  importaciones: ["modificar", "borrar"],
  // Configuración > Documentos: modificar = guardar/importar plantillas,
  // borrar = eliminarlas. "documentos" (generar) no tiene acciones.
  documentacion: ["modificar", "borrar"],
  // Facturación
  // modificar = aceptar, rechazar, editar y marcar utilizada;
  // borrar = anular la solicitud
  solicitudescredito: ["modificar", "borrar"],
  // borrar = eliminar motores facturados; versucursal = ver en el listado
  // de Ventas las ventas de TODAS las sucursales (sin el flag, el backend
  // fuerza la sucursal del usuario en /api/informemotoresfacturados)
  ares2010: ["borrar", "versucursal"],
  // versucursal = ver el cuadre de TODAS las sucursales (sin el flag, el
  // backend fuerza la sucursal del usuario en /api/cuadrecaja_completo y
  // /api/cuadrecaja_resumen)
  ares7070: ["versucursal"],
  // versucursal = ver en el Cuadre Desglosado (conteo de efectivo) los
  // conteos de TODAS las sucursales y filtrar por sucursal (sin el flag, el
  // backend fuerza la del usuario en /api/cuadrecaja_listar y el detalle);
  // borrar = eliminar un conteo desde su detalle (/api/cuadrecaja_eliminar);
  // modificar = cancelarlo (baja lógica, /api/cuadrecaja_cancelar)
  cuadrecaja: ["versucursal", "borrar", "modificar"],
  // versucursal = ver el beneficio de TODAS las sucursales sin ser admin.
  beneficiorecuperado: ["versucursal"],
  // versucursal = elegir cualquier sucursal en el Reporte de Morosidad sin
  // ser admin (sin el flag, solo la propia).
  reportemorosidad: ["versucursal"],
  // versucursal = elegir cualquier sucursal en el Informe de pagos (vigentes
  // y cancelados) sin ser admin (sin el flag, solo la propia).
  informepagos: ["versucursal"],
  cotizaciones: ["borrar"], // anular cotización
  informemotoresfacturados: ["modificar"], // cambio de chasis desde la consulta
  listadochasisincautados: ["modificar"], // cambiar chasis desde el expediente
  ares4025: ["borrar"], // anular cobro de inicial
  // Préstamos
  expediente: ["modificar"], // editar datos generales desde el diálogo de la venta
  informeprestamos: ["modificar"], // cambiar mora
  cancelarprestamos: ["modificar", "borrar"], // restaurar / cancelar préstamo
  gestioncobros: ["modificar"], // asignar/cambiar gestor de cobros
  // Pagos
  ares4010: ["borrar"], // cancelar pago
  // Incautos
  // En todo el módulo, versucursal = ver y operar TODAS las sucursales (sin
  // el flag, el backend fuerza la del usuario; app/alcanceincautos.py).
  ares5010: ["versucursal"], // registrar incauto (préstamos de todas)
  ares5030: ["versucursal"], // solicitudes de incauto por lote
  ares5060: ["versucursal"], // registrar cargo
  ares5080: ["borrar", "versucursal"], // borrar = cancelar incauto/cargo
  ares5085: ["versucursal"], // pagar incautos de todas las sucursales
  ares5090: ["borrar", "versucursal"], // borrar = anular recibo de pago a incautador
  ares5095: ["versucursal"], // reporte de incautos y cargos
  ares8070: ["borrar", "versucursal"], // borrar = eliminar registro de incauto
  cobrosincautosfechas: ["modificar", "borrar", "versucursal"],
  // Matrículas
  ares6040: ["borrar"], // cancelar cobro de matrícula
  solicitudmatriculas: ["borrar"], // cancelar solicitud / quitar chasis
  // modificar = bandeja de la central (aceptar, rechazar); versucursal =
  // ver y pedir por todas las sucursales.
  pedidomatriculas: ["modificar", "versucursal"],
  // Gastos
  ares5520: ["borrar"], // eliminar gasto (/api/eliminargasto)
  // Ventas generales
  ares9610: ["modificar", "borrar"], // categorías: guardar / eliminar
  ares9620: ["modificar", "borrar"], // cambiar número de GPS / cancelar venta
  ares9640: ["borrar"], // anular pago
  // Ventas x mayor
  ares9705: ["modificar"], // modificar cliente
  ares9720: ["borrar"], // anular factura / devolver chasis
  ares9740: ["borrar"], // anular cobro
  ares9755: ["modificar"], // saldar chasis
  ares9756: ["modificar", "borrar"], // precios / eliminar chasis
  // conduces consignados: modificar = entregar, marcar vendido y facturar;
  // borrar = devolver un motor en piso y anular el conduce
  conducesxmayor: ["modificar", "borrar"],
  // Cuentas x pagar
  ares8505: ["modificar"], // modificar suplidor
  ares8520: ["borrar"], // anular factura
  ares8540: ["borrar"], // anular pago
  ares8550: ["modificar", "borrar"], // precio de chasis / eliminar chasis
  // Taller
  ares9015: ["borrar"], // eliminar orden
  // Garantías: modificar = registrar servicios y editar políticas;
  // borrar = decidir servicios omitidos (mantener / marcar perdida) y
  // eliminar políticas
  garantias: ["modificar", "borrar"],
  // Configuración
  configuracion: ["modificar", "borrar"],
  // monedas: modificar = editar el catálogo (habilitar una moneda). Registrar
  // la tasa del día NO exige flag, basta con tener la fila del programa.
  monedas: ["modificar"],
  ares8060: ["borrar"], // eliminar abono a capital
  ares3030: ["borrar"], // eliminar descuento de interés
  ares8080: ["borrar"], // limpiar producto
  // Clientes / Suplidores (pantalla compartida entre préstamos y la app POS)
  registroclientessuplidores: ["modificar", "borrar"], // modificar / eliminar cliente
  clientessuplidores: ["modificar", "borrar"],
  configuracionclientessuplidores: ["modificar"],
  // App de Facturación POS: reglas de ventas y catálogos
  regladeventas: ["modificar", "borrar"], // tipos de documento: guardar-cerrar / eliminar
  reglasimpuestosventas: ["modificar", "borrar"],
  reglasimpuestos: ["modificar", "borrar"], // variante de la pantalla de productos
  areageograficaventas: ["modificar", "borrar"],
  areasgeograficaventas: ["modificar", "borrar"],
  descuentosgenerales: ["modificar", "borrar"],
  configuracionfacturas: ["modificar"],
  configuracionformaspago: ["modificar", "borrar"],
  configuraciongeneral: ["modificar", "borrar"],
  // App de Facturación POS: cuentas x cobrar
  consultapagosclientescredito: ["borrar"], // anular pago
  pagosclientescreditodetalledialog: ["borrar"], // anular pago desde el detalle
  // App de Facturación POS: productos no serializados
  mantenimientoproductos: ["modificar", "borrar"],
  gestionproductos: ["modificar", "borrar"], // umbrella de edición de productos
  listasprecios: ["modificar", "borrar"],
  cuentascontables: ["modificar", "borrar"],
  promociones: ["modificar", "borrar"],
  reglasdescuentos: ["modificar", "borrar"],
  transaccionesinventario: ["borrar"], // eliminar entrada de inventario
  conteofisicoinventario: ["modificar"], // cerrar conteo
  // Nómina
  configuracionnomina: ["modificar"], // guardar horarios y aportes
  catalogosnomina: ["modificar", "borrar"],
  departamentonomina: ["modificar", "borrar"],
  sectornomina: ["modificar", "borrar"],
  registroempleados: ["modificar"],
  inicionomina: ["modificar"],
  iniciarnomina: ["modificar"],
  definicionnomina: ["modificar", "borrar"],
  cerrarnomina: ["modificar"],
};

/**
 * Programas que el código consulta pero que no son ninguna `key` de
 * routes.js (no tienen entrada propia en el menú). Se resuelven contra el
 * módulo indicado para heredar sección y categoría.
 */
const COMPLEMENTO = [
  {
    programa: "cancelarprestamos",
    moduloKey: "prestamos",
    pantalla: "Gestión de Préstamos",
    etiqueta: "Cancelar / restaurar préstamos",
  },
  {
    programa: "ares7050",
    moduloKey: "pagos",
    pantalla: "Pagos",
    etiqueta: "Informe de pagos (permiso alterno)",
  },
  {
    // Permiso propio de la reimpresión de recibos, que antes colgaba de
    // ares4030 (Mantenimiento de Pagos, la pantalla que anula pagos): dejar
    // reimprimir obligaba a conceder también el poder de anular. Lo hace
    // cumplir el backend en /api/ares4030_reimprimirrecibo.
    programa: "reimprimirpagos",
    moduloKey: "pagos",
    pantalla: "Pagos",
    etiqueta: "Reimprimir recibos de pago",
  },
  {
    programa: "listadochasisincautados",
    moduloKey: "facturacion",
    pantalla: "Ventas",
    etiqueta: "Listado de chasis incautados",
  },
  {
    // Marcador negativo: el rol que tenga esta fila NO ve costos de compra
    // (informe de motores, inventario bulk, registro de chasis).
    programa: "ocultarcostos",
    moduloKey: "inventario",
    pantalla: "Inventario",
    etiqueta: "Ocultar costos (restricción)",
  },
  // registroclientessuplidores salió del menú principal (routes.js) y vive
  // en el de Facturación: lo registra la pasada de routesfac (pasada 4) bajo
  // su módulo Clientes Suplidores, con la categoría de la app POS. No va
  // aquí con categoria "facturacion": esa categoría combina flags con las
  // pantallas de Ventas de préstamos (puedeAccion) y concedería borrar por
  // rebote.
  {
    programa: "cuadrecajapos",
    moduloKey: "facturacion",
    pantalla: "Cuadre de Caja POS",
    etiqueta: "Cuadre de Caja (POS)",
  },
  {
    // Marcador positivo global: el rol que tenga esta fila ve TODAS las
    // sucursales en reportes y selectores, sin ser AdmG/Administrador.
    // Lo hace cumplir el backend en _perfil_reporte (permisos.py:
    // tiene_todas_sucursales).
    programa: "versucursales",
    moduloKey: "configuracion",
    pantalla: "Configuración",
    etiqueta: "Ver todas las sucursales",
  },
  {
    programa: "nomina",
    moduloKey: "nomina",
    pantalla: "Nómina",
    etiqueta: "Nómina (módulo)",
  },
  {
    programa: "configuracionnomina",
    moduloKey: "nomina",
    pantalla: "Configuración",
    etiqueta: "Configuración de nómina",
  },
  {
    programa: "catalogosnomina",
    moduloKey: "nomina",
    pantalla: "Departamentos y sectores",
    etiqueta: "Departamentos y sectores",
  },
  {
    programa: "departamentonomina",
    moduloKey: "nomina",
    pantalla: "Departamentos",
    etiqueta: "Departamentos",
  },
  {
    programa: "sectornomina",
    moduloKey: "nomina",
    pantalla: "Sectores",
    etiqueta: "Sectores",
  },
  {
    programa: "inicionomina",
    moduloKey: "nomina",
    pantalla: "Inicio",
    etiqueta: "Inicio",
  },
  {
    programa: "iniciarnomina",
    moduloKey: "nomina",
    pantalla: "Iniciar nómina",
    etiqueta: "Iniciar nómina",
  },
  {
    programa: "definicionnomina",
    moduloKey: "nomina",
    pantalla: "Definición de nómina",
    etiqueta: "Definición de nómina",
  },
  {
    programa: "cerrarnomina",
    moduloKey: "nomina",
    pantalla: "Cerrar nómina",
    etiqueta: "Cerrar nómina",
  },
];

/**
 * Programas de la app de Facturación POS que el código consulta pero que no
 * son keys de routesfac.js: pantallas hijas (se abren desde otra pantalla),
 * pestañas de una pantalla contenedora y umbrellas. Se resuelven contra el
 * módulo de routesfac indicado.
 */
const COMPLEMENTO_FAC = [
  // Hijas de Facturas Emitidas
  { programa: "mantenimientonotacredito", moduloKey: "ventas", pantalla: "Facturas Emitidas", etiqueta: "Notas de crédito (mantenimiento)" },
  { programa: "mantenimientonotadebito", moduloKey: "ventas", pantalla: "Facturas Emitidas", etiqueta: "Notas de débito (mantenimiento)" },
  // Pestañas de Reglas de Ventas
  { programa: "reglasimpuestosventas", moduloKey: "ventas", pantalla: "Reglas de Ventas", etiqueta: "Reglas de impuestos" },
  { programa: "areageograficaventas", moduloKey: "ventas", pantalla: "Reglas de Ventas", etiqueta: "Áreas geográficas" },
  { programa: "areasgeograficaventas", moduloKey: "ventas", pantalla: "Reglas de Ventas", etiqueta: "Áreas geográficas (alias)" },
  { programa: "descuentosgenerales", moduloKey: "ventas", pantalla: "Reglas de Ventas", etiqueta: "Descuentos generales" },
  // Cotizaciones de la app POS
  { programa: "listadocotizaciones", moduloKey: "ventas", pantalla: "Cotizaciones", etiqueta: "Listado de cotizaciones" },
  { programa: "eliminarcotizaciones", moduloKey: "ventas", pantalla: "Cotizaciones", etiqueta: "Eliminar cotizaciones" },
  // Cuentas x cobrar
  { programa: "consultapagosclientescredito", moduloKey: "cuentasxcobrar", pantalla: "Cuentas x Cobrar", etiqueta: "Consulta de pagos" },
  // Productos no serializados (pantallas que se abren desde Productos)
  { programa: "gestionproductos", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Gestión de productos (edición)" },
  { programa: "listadoproductos", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Listado de productos" },
  { programa: "listasprecios", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Listas de precios" },
  { programa: "conteofisicoinventario", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Conteo físico de inventario" },
  { programa: "cuentascontables", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Cuentas contables asociadas" },
  { programa: "promociones", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Promociones" },
  { programa: "transaccionesinventario", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Transacciones de inventario" },
  { programa: "reglasdescuentos", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Reglas de descuentos" },
  { programa: "transferenciasinventario", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Transferencias de inventario" },
  { programa: "agregardepartamentos", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Departamentos" },
  { programa: "reglasimpuestos", moduloKey: "mantenimientoproductos", pantalla: "Productos", etiqueta: "Reglas de impuestos (productos)" },
  // Configuración de clientes: pestaña de Clientes / Suplidores (pantalla
  // compartida con préstamos, su módulo se registra desde routesfac porque
  // routes.js la sacó del menú).
  { programa: "configuracionclientessuplidores", moduloKey: "clientessuplidores", pantalla: "Clientes / Suplidores", etiqueta: "Configuración de clientes" },
];

/**
 * Etiquetas para programas que no tienen ruta legacy con nombre humano.
 * El usuario no conoce los códigos "aresNNNN": ninguna etiqueta del catálogo
 * debe exponerlos (lo vigila un test). Significados tomados de permisos.py.
 */
const ETIQUETAS_EXTRA = {
  ares4035: "Iniciales (informe no cobrados)", // /api/reportes_inicialesnocobrados
  ares5510: "Gastos (registrar)", // /api/agregargasto
  ares5520: "Gastos (eliminar)", // /api/eliminargasto
  ares5530: "Gastos (consultar)", // /api/consultargastos
  beneficiorecuperado: "Beneficio recuperado (costos y márgenes)", // /api/beneficiorecuperado
  documentacion: "Configuración (documentación)", // editor de plantillas
  documentos: "Documentos (generar desde el expediente)",
  monedas: "Monedas y tasa del día",
  departamentonomina: "Departamentos",
  sectornomina: "Sectores",
};

/** Quita la coletilla "(ruta vieja)" de los nombres de entradas legacy. */
function limpiarNombre(nombre) {
  return String(nombre || "")
    .replace(/\s*\(ruta vieja\)/i, "")
    .replace(/,\s*ruta vieja/i, "")
    .trim();
}

/** Programas que declara una entrada del menú (pantallas unificadas). */
function keysDe(entrada) {
  if (Array.isArray(entrada.keys) && entrada.keys.length) return entrada.keys;
  const unica = entrada.programa ?? entrada.key;
  return unica ? [unica] : [];
}

let cachePorPrograma = null;
let cacheModulos = null;

function construir() {
  const porPrograma = new Map();
  const modulos = new Map(); // moduloKey -> { modulo, moduloKey, seccion, categoria }

  // Pasada 1: las entradas ocultas "-legacy" conservan los nombres humanos
  // de las pantallas viejas; sirven de etiqueta para los sub-programas de
  // las pantallas unificadas (ej. ares2015 -> "Ventas (contado)").
  const etiquetas = new Map();
  const recogerEtiquetas = (lista) => {
    (lista || []).forEach((entrada) => {
      if (entrada?.key && String(entrada.key).endsWith("-legacy")) {
        const base = String(entrada.key).replace(/-legacy$/, "");
        if (!etiquetas.has(base)) etiquetas.set(base, limpiarNombre(entrada.name));
      }
      if (Array.isArray(entrada?.collapse)) recogerEtiquetas(entrada.collapse);
    });
  };
  recogerEtiquetas(routes);

  // Una pantalla puede listar en `keys` la clave de OTRO módulo del menú
  // como acceso prestado mientras se siembra su programa propio (ej.
  // marcasmodelos acepta "configuracion", como conteos aceptó la fila
  // vieja). Esa key prestada pertenece a su módulo dueño: aquí se salta
  // para que el catálogo no la registre en el módulo que la presta.
  const clavesDeModulo = new Set(
    routes.filter((r) => r?.type === "collapse" && !EXCLUIR.has(r.key)).map((r) => r.key)
  );

  const agregarPantalla = (pantalla, modulo, seccion, categoriaModulo) => {
    const principal = pantalla.programa ?? pantalla.key;
    keysDe(pantalla).forEach((k) => {
      if (!k || porPrograma.has(k)) return;
      if (k !== principal && clavesDeModulo.has(k)) return;
      porPrograma.set(k, {
        programa: k,
        etiqueta:
          k === principal
            ? pantalla.name
            : etiquetas.get(k) || ETIQUETAS_EXTRA[k] || `${pantalla.name} (${k})`,
        pantalla: pantalla.name,
        modulo: modulo.name,
        moduloKey: modulo.key,
        seccion,
        categoria: CATEGORIA_PROGRAMA[k] ?? categoriaModulo,
        acciones: ACCIONES_PROGRAMA[k] || [],
      });
    });
  };

  // Pasada 2: secciones -> módulos -> pantallas.
  let seccion = "General";
  routes.forEach((r) => {
    if (r?.type === "title") {
      seccion = r.title;
      return;
    }
    if (r?.type !== "collapse" || EXCLUIR.has(r.key)) return;
    const categoriaModulo = CATEGORIA_MODULO[r.key] ?? r.programa ?? r.key;
    modulos.set(r.key, { modulo: r.name, moduloKey: r.key, seccion, categoria: categoriaModulo });
    const hijos = Array.isArray(r.collapse) ? r.collapse.filter((h) => h?.type === "collapse") : [];
    if (!hijos.length) {
      // Módulos de una sola pantalla (Inicio, DataOrbit, Configuración).
      agregarPantalla(r, r, seccion, categoriaModulo);
    } else {
      hijos.forEach((h) => agregarPantalla(h, r, seccion, categoriaModulo));
    }
  });

  // Nómina vive en routesnom.js (módulo aparte, como Facturación).
  // Varios collapse hermanos (Nómina, Configuración) se agrupan en un solo
  // módulo de roles para que el asistente no parta el permiso.
  const gruposNomina = (routesNom || []).filter(
    (r) => r?.type === "collapse" && !EXCLUIR.has(r.key) && r.key !== "dashboard"
  );
  if (gruposNomina.length) {
    if (!modulos.has("nomina")) {
      const grupoUmbrella = gruposNomina.find((g) => g.key === "nomina") || gruposNomina[0];
      modulos.set("nomina", {
        modulo: "Nómina",
        moduloKey: "nomina",
        seccion: "Nómina",
        categoria: grupoUmbrella.programa || "nomina",
      });
    }
    const moduloNomina = { name: "Nómina", key: "nomina", programa: "nomina" };
    gruposNomina.forEach((grupo) => {
      const hijosNomina = Array.isArray(grupo.collapse)
        ? grupo.collapse.filter((h) => h?.type === "collapse")
        : [];
      if (!hijosNomina.length) {
        agregarPantalla(grupo, moduloNomina, "Nómina", "nomina");
        return;
      }
      hijosNomina.forEach((h) => agregarPantalla(h, moduloNomina, "Nómina", "nomina"));
    });
  }

  // Pasada 3: programas consultados por el código sin entrada de menú.
  COMPLEMENTO.forEach((c) => {
    if (porPrograma.has(c.programa)) return;
    const m = modulos.get(c.moduloKey) || {
      modulo: "Otros",
      moduloKey: "otros",
      seccion: "Otros",
      categoria: "",
    };
    porPrograma.set(c.programa, {
      programa: c.programa,
      etiqueta: c.etiqueta,
      pantalla: c.pantalla,
      modulo: m.modulo,
      moduloKey: m.moduloKey,
      seccion: m.seccion,
      categoria: m.categoria,
      acciones: ACCIONES_PROGRAMA[c.programa] || [],
    });
  });

  // Pasada 4: la app de Facturación POS (routesfac.js). Sus filas llevan la
  // categoría propia CATEGORIA_FAC. Los módulos que routesfac comparte con
  // routes.js (Clientes / Suplidores) ya quedaron registrados arriba con su
  // módulo dueño y aquí se saltan, igual que cualquier programa repetido.
  const SECCION_FAC = "Facturación POS";
  routesFac.forEach((r) => {
    if (r?.type !== "collapse" || EXCLUIR_FAC.has(r.key)) return;
    if (modulos.has(r.key)) return; // módulo compartido con routes.js
    modulos.set(r.key, {
      modulo: r.name,
      moduloKey: r.key,
      seccion: SECCION_FAC,
      categoria: CATEGORIA_FAC,
    });
    const modulo = modulos.get(r.key);
    const hijos = Array.isArray(r.collapse) ? r.collapse.filter((h) => h?.type === "collapse") : [];
    const pantallas = hijos.length ? hijos : [r];
    pantallas.forEach((pantalla) => {
      keysDe(pantalla).forEach((k) => {
        if (!k || porPrograma.has(k)) return;
        porPrograma.set(k, {
          programa: k,
          etiqueta: pantalla.name,
          pantalla: pantalla.name,
          modulo: modulo.modulo,
          moduloKey: modulo.moduloKey,
          seccion: SECCION_FAC,
          categoria: CATEGORIA_FAC,
          acciones: ACCIONES_PROGRAMA[k] || [],
        });
      });
    });
  });

  COMPLEMENTO_FAC.forEach((c) => {
    if (porPrograma.has(c.programa)) return;
    const m = modulos.get(c.moduloKey) || {
      modulo: "Facturación POS",
      moduloKey: "otros-fac",
      seccion: SECCION_FAC,
      categoria: CATEGORIA_FAC,
    };
    porPrograma.set(c.programa, {
      programa: c.programa,
      etiqueta: c.etiqueta,
      pantalla: c.pantalla,
      modulo: m.modulo,
      moduloKey: m.moduloKey,
      seccion: m.seccion,
      categoria: CATEGORIA_FAC,
      acciones: ACCIONES_PROGRAMA[c.programa] || [],
    });
  });

  cachePorPrograma = porPrograma;
  cacheModulos = modulos;
}

/** Todas las entradas del catálogo, en el orden del menú. */
export function getCatalogoProgramas() {
  if (!cachePorPrograma) construir();
  return [...cachePorPrograma.values()];
}

/** Entrada del catálogo para un programa, o null si no se reconoce. */
export function getEntradaPrograma(programa) {
  if (!cachePorPrograma) construir();
  return cachePorPrograma.get(String(programa || "").trim()) || null;
}

/**
 * Catálogo agrupado para pintar el árbol Sección -> Módulo -> Programas.
 * @returns {Array<{seccion: string, modulo: string, moduloKey: string, programas: Array}>}
 */
export function getCatalogoModulos() {
  if (!cachePorPrograma) construir();
  const grupos = new Map();
  [...cacheModulos.values()].forEach((m) => {
    grupos.set(m.moduloKey, { ...m, programas: [] });
  });
  getCatalogoProgramas().forEach((p) => {
    if (!grupos.has(p.moduloKey)) {
      grupos.set(p.moduloKey, {
        modulo: p.modulo,
        moduloKey: p.moduloKey,
        seccion: p.seccion,
        categoria: p.categoria,
        programas: [],
      });
    }
    grupos.get(p.moduloKey).programas.push(p);
  });
  return [...grupos.values()].filter((g) => g.programas.length);
}
